import { useCallback, useEffect, useRef } from 'react';
import type { RefCallback } from 'react';

import { Player } from '@atm/terminal-webrtc-player';

type PlayerOnStatusChange = NonNullable<Player['onStatusChange']>;
export type PlayerStatus = Parameters<PlayerOnStatusChange>[0];
type PlayerStatusReason = Parameters<PlayerOnStatusChange>[1];
export type PlayerStatusChangeHandler = (status: PlayerStatus, reason?: PlayerStatusReason) => void;

type UseGameStreamPlayerParams = {
  onStatusChange?: PlayerStatusChangeHandler;
  streamName: string;
  streamMuted: boolean;
  whepUrl: string | undefined;
};

export const useGameStreamPlayer = ({
  onStatusChange,
  streamName,
  streamMuted,
  whepUrl,
}: UseGameStreamPlayerParams): RefCallback<HTMLVideoElement> => {
  const videoElementRef = useRef<HTMLVideoElement | null>(null);
  const videoRef = useCallback((node: HTMLVideoElement | null) => {
    videoElementRef.current = node;
  }, []);

  useEffect(() => {
    const videoElement = videoElementRef.current;

    if (!videoElement || !streamName) {
      return;
    }

    const shouldEnableSound = !streamMuted;
    let isDisposed = false;

    const forceDisableControls = () => {
      videoElement.controls = false;
      videoElement.removeAttribute('controls');
    };

    forceDisableControls();

    const controlsObserver = new MutationObserver(forceDisableControls);
    controlsObserver.observe(videoElement, {
      attributes: true,
      attributeFilter: ['controls'],
    });

    videoElement.addEventListener('loadedmetadata', forceDisableControls);

    const player = new Player({
      videoElement,
      whepUrl,
    });

    player.onStatusChange = onStatusChange;
    // Always start muted to avoid autoplay blocking by the browser.
    player.mute();

    const resumeAudioOnInteraction = () => {
      if (!shouldEnableSound) {
        return;
      }

      player.unmute();
      void videoElement.play().catch(() => {
        // Browser may still block autoplay until next user interaction.
      });
    };

    if (shouldEnableSound) {
      window.addEventListener('pointerdown', resumeAudioOnInteraction, { once: true });
      window.addEventListener('keydown', resumeAudioOnInteraction, { once: true });
    }

    void player.start(streamName).then((result) => {
      if (!isDisposed && result instanceof Error) {
        if (!String(result.message).includes('start canceled')) {
          console.error('Failed to start GameStream:', result);
        }
      }
    });

    return () => {
      isDisposed = true;
      controlsObserver.disconnect();
      videoElement.removeEventListener('loadedmetadata', forceDisableControls);
      window.removeEventListener('pointerdown', resumeAudioOnInteraction);
      window.removeEventListener('keydown', resumeAudioOnInteraction);

      try {
        player.stop();
      } catch {
        // Player may not have initialized peer connection yet in StrictMode cleanup.
      }
    };
  }, [onStatusChange, streamMuted, streamName, whepUrl]);

  return videoRef;
};
